import { Button, Text } from '@innovationdepartment/proxima-ui';
import { AddRounded } from '@mui/icons-material';
import SectionLayout from 'ui/SectionLayout';
import {
  EmptyState,
  HeaderLabels,
  List,
  LoadErrorRow,
  Pagination
} from '../../components/shared.styles';
import type { AudienceRowModel } from '../../lib/buildAudienceRows';
import AudienceListRow from './AudienceListRow';

type AudienceListViewProps = {
  rows: AudienceRowModel[];
  loading: boolean;
  error: string | null;
  /** True while a Retry of a failed load is running. */
  retrying: boolean;
  updatingDefinitionId: number | null;
  pageNumber: number;
  pageCount: number;
  onPageChange: (pageNumber: number) => void;
  onRetry: () => void;
  onNewAudience: () => void;
  onOpenDefinition: (definitionId: number) => void;
  onSetPaused: (definitionId: number, paused: boolean) => void;
};

const AudienceListView = ({
  rows,
  loading,
  error,
  retrying,
  updatingDefinitionId,
  pageNumber,
  pageCount,
  onPageChange,
  onRetry,
  onNewAudience,
  onOpenDefinition,
  onSetPaused
}: AudienceListViewProps) => (
  <SectionLayout
    title="Custom Audiences"
    titleActions={
      <Button
        label="New custom audience"
        size="small"
        startIcon={<AddRounded />}
        onClick={onNewAudience}
      />
    }
  >
    {error ? (
      <LoadErrorRow role="alert">
        <Text variant="body2" color="red60">
          Failed to load custom audiences: {error}
        </Text>
        <Button
          label={retrying ? 'Retrying…' : 'Retry'}
          variant="outlined"
          size="small"
          disabled={retrying}
          onClick={onRetry}
        />
      </LoadErrorRow>
    ) : null}
    {!error && !loading && rows.length === 0 ? (
      <EmptyState>
        <Text variant="body1Semibold">No custom audiences yet. Describe one to get started.</Text>
        <Button
          label="New custom audience"
          size="small"
          startIcon={<AddRounded />}
          onClick={onNewAudience}
        />
      </EmptyState>
    ) : null}
    {rows.length > 0 ? (
      <List>
        <HeaderLabels>
          <span>Name</span>
          <span>Status</span>
          <span>Delivery</span>
          <span>Created</span>
          <span>Last run</span>
        </HeaderLabels>
        {rows.map(row => (
          <AudienceListRow
            key={row.definition.id}
            row={row}
            updating={updatingDefinitionId === row.definition.id}
            onOpen={() => onOpenDefinition(row.definition.id)}
            onSetPaused={paused => onSetPaused(row.definition.id, paused)}
          />
        ))}
      </List>
    ) : null}
    {pageCount > 1 ? (
      <Pagination>
        <Button
          label="Previous"
          variant="text"
          size="small"
          disabled={pageNumber <= 1}
          onClick={() => onPageChange(pageNumber - 1)}
        />
        <Text variant="body2" color="neutral60">
          Page {pageNumber} of {pageCount}
        </Text>
        <Button
          label="Next"
          variant="text"
          size="small"
          disabled={pageNumber >= pageCount}
          onClick={() => onPageChange(pageNumber + 1)}
        />
      </Pagination>
    ) : null}
  </SectionLayout>
);

export default AudienceListView;
